import { CLAUSE_TYPE_COMPOUND, CLAUSE_TYPE_TERMINAL, NODE_TYPE_CLAUSE, NODE_TYPE_OPERAND, NODE_TYPE_ORDER_BY, NODE_TYPE_QUERY, OPERAND_TYPE_FUNCTION, OPERAND_TYPE_KEYWORD, OPERAND_TYPE_LIST, OPERAND_TYPE_VALUE, ORDER_BY_OPERATOR_ORDER_BY } from '../constants';

/**
 * AstNode accept functions. We define them outside of the NodeCreators so the same function reference is returned when
 * a creator is invoked multiple times (which is helpful when testing for equality of AST objects).
 */
function acceptQuery(visitor) {
  return visitor.visitQuery ? visitor.visitQuery(this) : visitor.visitNode(this);
}

function acceptCompoundClause(visitor) {
  return visitor.visitCompoundClause ? visitor.visitCompoundClause(this) : visitor.visitNode(this);
}

function acceptCompoundOperator(visitor) {
  return visitor.visitCompoundOperator ? visitor.visitCompoundOperator(this) : visitor.visitNode(this);
}

function acceptTerminalClause(visitor) {
  return visitor.visitTerminalClause ? visitor.visitTerminalClause(this) : visitor.visitNode(this);
}

function acceptPredicate(visitor) {
  return visitor.visitPredicate ? visitor.visitPredicate(this) : visitor.visitNode(this);
}

function acceptPredicateOperator(visitor) {
  return visitor.visitPredicateOperator ? visitor.visitPredicateOperator(this) : visitor.visitNode(this);
}

function acceptField(visitor) {
  return visitor.visitField ? visitor.visitField(this) : visitor.visitNode(this);
}

function acceptProperty(visitor) {
  return visitor.visitProperty ? visitor.visitProperty(this) : visitor.visitNode(this);
}

function acceptOperator(visitor) {
  return visitor.visitOperator ? visitor.visitOperator(this) : visitor.visitNode(this);
}

function acceptValueOperand(visitor) {
  return visitor.visitValueOperand ? visitor.visitValueOperand(this) : visitor.visitNode(this);
}

function acceptKeywordOperand(visitor) {
  return visitor.visitKeywordOperand ? visitor.visitKeywordOperand(this) : visitor.visitNode(this);
}

function acceptFunctionOperand(visitor) {
  return visitor.visitFunctionOperand ? visitor.visitFunctionOperand(this) : visitor.visitNode(this);
}

function acceptFunction(visitor) {
  return visitor.visitFunction ? visitor.visitFunction(this) : visitor.visitNode(this);
}

function acceptArgument(visitor) {
  return visitor.visitArgument ? visitor.visitArgument(this) : visitor.visitNode(this);
}

function acceptListOperand(visitor) {
  return visitor.visitListOperand ? visitor.visitListOperand(this) : visitor.visitNode(this);
}

function acceptOrderBy(visitor) {
  return visitor.visitOrderBy ? visitor.visitOrderBy(this) : visitor.visitNode(this);
}

function acceptOrderByOperator(visitor) {
  return visitor.visitOrderByOperator ? visitor.visitOrderByOperator(this) : visitor.visitNode(this);
}

function acceptOrderByField(visitor) {
  return visitor.visitOrderByField ? visitor.visitOrderByField(this) : visitor.visitNode(this);
}

function acceptOrderByDirection(visitor) {
  return visitor.visitOrderByDirection ? visitor.visitOrderByDirection(this) : visitor.visitNode(this);
}
/**
 * Methods used to create an AST node of a given type.
 */


export var NodeCreators = {
  Query: function Query(where, orderBy) {
    return {
      type: NODE_TYPE_QUERY,
      where: where,
      orderBy: orderBy,
      accept: acceptQuery
    };
  },
  CompoundClause: function CompoundClause(operator, clauses, position) {
    return {
      type: NODE_TYPE_CLAUSE,
      clauseType: CLAUSE_TYPE_COMPOUND,
      operator: operator,
      clauses: clauses,
      position: position,
      accept: acceptCompoundClause
    };
  },
  CompoundOperator: function CompoundOperator(value, positions) {
    return {
      value: value,
      positions: positions,
      accept: acceptCompoundOperator
    };
  },
  TerminalClause: function TerminalClause(field, operator, operand, predicates, position) {
    return {
      type: NODE_TYPE_CLAUSE,
      clauseType: CLAUSE_TYPE_TERMINAL,
      field: field,
      operator: operator,
      operand: operand,
      predicates: predicates,
      position: position,
      accept: acceptTerminalClause
    };
  },
  Predicate: function Predicate(operator, operand, position) {
    return {
      operator: operator,
      operand: operand,
      position: position,
      accept: acceptPredicate
    };
  },
  PredicateOperator: function PredicateOperator(text, value, position) {
    return {
      text: text,
      value: value,
      position: position,
      accept: acceptPredicateOperator
    };
  },
  Field: function Field(text, value, properties, position) {
    return {
      text: text,
      value: value,
      properties: properties,
      position: position,
      accept: acceptField
    };
  },
  Property: function Property(key, path, position) {
    return {
      key: key,
      path: path,
      position: position,
      accept: acceptProperty
    };
  },
  Operator: function Operator(text, value, position) {
    return {
      text: text,
      value: value,
      position: position,
      accept: acceptOperator
    };
  },
  ValueOperand: function ValueOperand(text, value, position) {
    return {
      type: NODE_TYPE_OPERAND,
      operandType: OPERAND_TYPE_VALUE,
      text: text,
      value: value,
      position: position,
      accept: acceptValueOperand
    };
  },
  KeywordOperand: function KeywordOperand(value, position) {
    return {
      type: NODE_TYPE_OPERAND,
      operandType: OPERAND_TYPE_KEYWORD,
      value: value,
      position: position,
      accept: acceptKeywordOperand
    };
  },
  FunctionOperand: function FunctionOperand(functionString, args, position) {
    return {
      type: NODE_TYPE_OPERAND,
      operandType: OPERAND_TYPE_FUNCTION,
      function: functionString,
      arguments: args,
      position: position,
      accept: acceptFunctionOperand
    };
  },
  FunctionString: function FunctionString(text, value, position) {
    return {
      text: text,
      value: value,
      position: position,
      accept: acceptFunction
    };
  },
  Argument: function Argument(text, value, position) {
    return {
      text: text,
      value: value,
      position: position,
      accept: acceptArgument
    };
  },
  ListOperand: function ListOperand(values, position) {
    return {
      type: NODE_TYPE_OPERAND,
      operandType: OPERAND_TYPE_LIST,
      values: values,
      position: position,
      accept: acceptListOperand
    };
  },
  OrderBy: function OrderBy(operator, fields, position) {
    return {
      type: NODE_TYPE_ORDER_BY,
      operator: operator,
      fields: fields,
      position: position,
      accept: acceptOrderBy
    };
  },
  OrderByOperator: function OrderByOperator(position) {
    return {
      value: ORDER_BY_OPERATOR_ORDER_BY,
      position: position,
      accept: acceptOrderByOperator
    };
  },
  OrderByField: function OrderByField(field, direction, position) {
    return {
      field: field,
      direction: direction,
      position: position,
      accept: acceptOrderByField
    };
  },
  OrderByDirection: function OrderByDirection(value, position) {
    return {
      value: value,
      position: position,
      accept: acceptOrderByDirection
    };
  }
};